import {v4 as uuidv4} from 'uuid';
import {CountTriggerTextViewerCoreOption} from '../type/CountTriggerTextViewerCoreOption.interface';
import {HtmlInfo} from '../type/HtmlInfo.interface';
import {makeFrameElements} from '../type/makeFrameElements';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {applyContentInlineStyles} from './applyContentInlineStyles';
import {applyHtmlInfo} from './applyHtmlInfo';
import {applySettingStylesForPageMode} from './applySettingStylesForPageMode';
import {applySettingStylesForScrollMode} from './applySettingStylesForScrollMode';
import {getPageTextViewerSizes} from './getPageTextViewerSizes';
import {initializeFrameElements} from './initializeFrameElements';
import {reviseImagesInContentElement} from './reviseImagesInContentElement.function';

export async function getPageCount({
  htmlInfo,
  option: countTriggerOption,
  debug,
  counterId,
  readType,
}: {
  htmlInfo: HtmlInfo;
  option: CountTriggerTextViewerCoreOption;
  debug?: boolean;
  counterId?: string;
  readType: TextViewerReadType;
}) {
  if (counterId) {
    const prevCounterElements = document.querySelectorAll(
      `[data-counter=${counterId}]`
    );
    for (const prevCounterElement of prevCounterElements) {
      prevCounterElement.parentElement?.removeChild(prevCounterElement);
    }
  }

  const option = {
    ...countTriggerOption,
    // NOTE: backgroundColor는 applySettingStyles에서 사용하지 않지만, option 타입 충족을 위해 유지
    backgroundColor: 'rgb(var(--colors-viewer-static-epub-white-bg))',
    textColor: 'rgb(var(--colors-viewer-static-epub-white-text))',
    isDark: false,
  };
  const frameElements = makeFrameElements();
  initializeFrameElements(frameElements);

  const {
    documentFragment,
    shadowHostElement,
    contentElement,
    containerElement,
  } = frameElements;

  const dataCounterId = counterId || uuidv4();
  shadowHostElement.setAttribute('data-counter', dataCounterId);

  if (debug) {
    shadowHostElement.style.visibility = 'visible';
  } else {
    shadowHostElement.style.position = 'absolute';
    shadowHostElement.style.visibility = 'invisible';
    shadowHostElement.style.left = '-10000px';
    shadowHostElement.style.top = '-10000px';
  }

  // 코드조각을 DOM에 붙입니다.

  document.body.appendChild(documentFragment);

  applyHtmlInfo({
    frameElements,
    htmlInfo,
  });

  if (readType === TextViewerReadType.Page) {
    applySettingStylesForPageMode({frameElements, option});
  } else {
    applySettingStylesForScrollMode({frameElements, option});
  }

  // 이미지가 정확한 크기를 가지도록 수정합니다.
  // 이때는 사이즈만 처리하면 되므로, 원본 이미지를 보여주는 부분은 수행하지 않습니다.

  await reviseImagesInContentElement({
    frameElements,
    option,
    showOriginalSrc: false,
    isPageTextViewer: true,
  });

  // 옵션에 따른 컨텐츠의 스타일을 적용합니다.

  applyContentInlineStyles({frameElements, option});

  // 페이지를 계산합니다.

  let pageCount;

  if (readType === TextViewerReadType.Page) {
    const {onePageOffset, columnGap} = getPageTextViewerSizes(option);

    // scrollWidth는 컨텐츠 양에 딱 맞게 늘어나니 항상 마지막 페이지의 columnGap 만큼 빈 공간이 없습니다.
    // 따라서 columnGap을 붙여 한 페이지 Offset으로 나눠 페이지 수를 구합니다.
    pageCount = Math.ceil(
      (contentElement.scrollWidth + columnGap) / onePageOffset
    );
  } else {
    pageCount = Math.ceil(contentElement.scrollHeight / option.size.height);
  }

  // debug 모드에서는 계산하기 위해 DOM에 붙였던 코드 조각을 유지하고, 컨테이너 overflow 되는 부분이 보이도록 스타일을 변경합니다.
  if (debug) {
    containerElement.style.overflow = '';
  }
  // debug 모드가 아니면 DOM에 붙였던 코드 조각을 제거합니다.
  else {
    document.body.removeChild(shadowHostElement);
  }

  return pageCount;
}
